import React, { useCallback, useEffect, useState } from 'react';
import { Link, useLocation, useNavigate, useParams } from 'react-router-dom';
import useStore from '../../store/useStore';
import { datasetsAPI, savedReportsAPI, kpisAPI, alertsAPI, statementsAPI } from '../../services/api';
import MeasuresEditor from './MeasuresEditor';
import AddDataPanel from './AddDataPanel';
import { FRESHNESS_LABEL, CADENCE_LABEL, COLUMN_TYPE_LABEL, datasetPath, definedBy, formatDateTime, formatNumber } from './dataSourceFormat';
import '../../styles/DatasetManagement.css';
import '../../styles/DataSources.css';
import '../../styles/DatasetBuilder.css';

const usesDataset = (members, name) => members.some((m) => typeof m === 'string' && m.startsWith(`${name}.`));

function reportMembers(report) {
  const q = report.cubeQuery || {};
  return [...(q.measures || []), ...(q.dimensions || []), ...(q.timeDimensions || []).map((t) => t.dimension)];
}

/**
 * One dataset: what it holds, how it is linked and kept fresh, its custom
 * measures, a sample of rows, and what is built on it.
 */
const DatasetDetailPage = () => {
  const { name } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const companyId = useStore((state) => state.currentCompany?.id);
  const user = useStore((state) => state.user);
  const isAdmin = user?.role === 'admin' || user?.role === 'super_admin';

  const [state, setState] = useState({ loading: true, error: '', dataset: null, schema: null, sample: [], usedBy: null });
  const [message, setMessage] = useState(location.state?.notice || '');
  const [confirmDelete, setConfirmDelete] = useState('');

  const describeError = (err, fallback) => err?.response?.data?.error || err?.message || fallback;

  const load = useCallback(async () => {
    if (!companyId) return;
    try {
      setState((s) => ({ ...s, loading: true, error: '' }));
      const ok = (p) => p.then((r) => (r?.success ? r.data : null)).catch(() => null);
      const [list, schema, sample, reports, kpis, rules, statements] = await Promise.all([
        ok(datasetsAPI.list(companyId)),
        ok(datasetsAPI.getSchema(name, companyId)),
        ok(datasetsAPI.query(name, companyId, { limit: 5 })),
        ok(savedReportsAPI.list(companyId)),
        ok(kpisAPI.list(companyId)),
        ok(alertsAPI.rules(companyId)),
        ok(statementsAPI.list(companyId)),
      ]);
      const dataset = (list || []).find((d) => d.name === name);
      if (!dataset) throw new Error(`There is no dataset called ${name}`);
      setState({
        loading: false,
        error: '',
        dataset,
        schema,
        sample: sample?.rows || [],
        usedBy: {
          reports: (reports || []).filter((r) => r.datasetName === name || usesDataset(reportMembers(r), name)),
          kpis: (kpis || []).filter((k) => usesDataset([k.measure, k.timeDimension], name)),
          alerts: (rules || []).filter((r) => r.kind === 'measure' && usesDataset([r.measure], name)),
          statements: (statements || []).filter((s) => s.datasetName === name),
        },
      });
    } catch (err) {
      setState((s) => ({ ...s, loading: false, error: describeError(err, 'Could not load the dataset') }));
    }
  }, [companyId, name]);

  useEffect(() => { load(); }, [load]);

  const remove = async () => {
    try {
      const res = await datasetsAPI.delete(name, companyId);
      if (!res?.success) throw new Error(res?.error || 'Delete failed');
      navigate('/admin/data-sources', { replace: true, state: { notice: `Deleted dataset ${name}` } });
    } catch (err) {
      setState((s) => ({ ...s, error: describeError(err, 'Could not delete the dataset') }));
    }
  };

  const { dataset, schema, sample, usedBy } = state;
  const hasDefinition = Array.isArray(schema?.columns);

  return (
    <div className="dataset-management data-source-detail">
      <nav className="breadcrumb" aria-label="Breadcrumb">
        <Link to="/admin/data-sources">Data sources</Link> <span aria-hidden="true">›</span> <span>{name}</span>
      </nav>

      <div className="page-header">
        <div>
          <h2>{name}</h2>
          <p className="page-subtitle">{dataset?.title ? `${dataset.title} · ` : ''}Defined by: {definedBy(dataset)}</p>
        </div>
        <div className="page-actions">
          <button className="btn-primary" onClick={load}>Refresh</button>
        </div>
      </div>

      {state.error && <div className="error-message" role="alert">{state.error}</div>}
      {message && <div className="success-message" role="status">{message}<button className="error-dismiss" onClick={() => setMessage('')} aria-label="Dismiss">×</button></div>}

      {state.loading && !dataset ? <div className="loading-spinner" /> : dataset && (
        <>
          <dl className="source-summary">
            <div><dt>Rows</dt><dd>{formatNumber(dataset.rowCount)}</dd></div>
            <div><dt>Columns</dt><dd>{formatNumber(dataset.columnCount)}</dd></div>
            <div><dt>Expected every</dt><dd>{CADENCE_LABEL[dataset.cadence] || dataset.cadence}</dd></div>
            <div><dt>Last received</dt><dd>{formatDateTime(dataset.lastSyncedAt)}</dd></div>
            <div><dt>Freshness</dt><dd><span className={`dataset-badge freshness-${dataset.freshness}`} style={{ marginLeft: 0 }}>{FRESHNESS_LABEL[dataset.freshness] || dataset.freshness}</span></dd></div>
            {dataset.openLoads > 0 && <div><dt>Load in progress</dt><dd>{dataset.openLoads}</dd></div>}
          </dl>

          {hasDefinition && (
            <section className="source-card" aria-labelledby="definition-heading">
              <div className="builder-section-head">
                <h3 id="definition-heading">Definition</h3>
                {isAdmin && schema.definitionSource !== 'pack' && (
                  <Link className="btn-secondary btn-small" to={`${datasetPath(name)}/edit`}>Edit definition</Link>
                )}
              </div>
              <p className="source-hint" style={{ marginTop: 0 }}>
                {schema.definitionSource === 'pack' && `Defined by ${schema.packTitle ? `the ${schema.packTitle} pack` : 'its pack'}, which keeps the columns up to date. Add your own measures below.`}
                {schema.definitionSource !== 'pack' && 'Defined by your company. Uploads and pushes must match these columns, or they are refused.'}
              </p>
              {schema.description && <p>{schema.description}</p>}
              <table className="datasets-table definition-view">
                <thead><tr><th>Column</th><th>Type</th><th>Description</th></tr></thead>
                <tbody>
                  {schema.columns.map((c) => (
                    <tr key={c.name}>
                      <td>
                        <code>{c.name}</code>
                        {schema.primaryKey === c.name && <span className="dataset-badge info">key</span>}
                        {c.required && schema.primaryKey !== c.name && <span className="dataset-badge">required</span>}
                      </td>
                      <td>{COLUMN_TYPE_LABEL[c.type] || c.type}</td>
                      <td>{c.description || ''}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              <h4>Links to other datasets</h4>
              {schema.relationships?.length ? (
                <ul className="dataset-relationships">
                  {schema.relationships.map((r) => (
                    <li key={`${r.column}-${r.refDataset}-${r.refColumn}`}>
                      Each row&apos;s <code>{r.column}</code> matches <Link to={datasetPath(r.refDataset)}>{r.refDataset}</Link> › <code>{r.refColumn}</code>
                      {!r.resolved && <span className="dataset-badge warn">{r.refDataset} does not exist</span>}
                    </li>
                  ))}
                </ul>
              ) : <p className="dataset-empty">None. Reports can combine this dataset with others once it is linked to them.</p>}
            </section>
          )}

          {hasDefinition && isAdmin && schema.definitionSource !== 'pack' && (
            <AddDataPanel schema={schema} companyId={companyId} onUploaded={load} initialFile={location.state?.uploadFile || null} />
          )}
          {hasDefinition && schema.definitionSource === 'pack' && (
            <section className="source-card">
              <h3>Data</h3>
              <p className="source-hint" style={{ marginTop: 0 }}>
                The Kinspeed Connect plugin for {schema.packTitle || 'this pack'} fills this dataset on its schedule.
              </p>
            </section>
          )}

          <section className="source-card">
            <MeasuresEditor datasetName={name} companyId={companyId} columns={schema?.columns || []} isAdmin={isAdmin} onChanged={load} />
          </section>

          {usedBy && (
            <section className="source-card" aria-labelledby="used-by-heading">
              <h3 id="used-by-heading">Built on this dataset</h3>
              {usedBy.reports.length + usedBy.kpis.length + usedBy.alerts.length + usedBy.statements.length === 0 ? (
                <p className="dataset-empty">Nothing yet. Build a report from it on the Reports page.</p>
              ) : (
                <ul className="used-by">
                  {usedBy.statements.map((s) => <li key={`s-${s.key}`}><span className="used-by-kind">Statement</span><Link to={`/statements/${encodeURIComponent(name)}/${s.key}`}>{s.title}</Link></li>)}
                  {usedBy.reports.map((r) => <li key={`r-${r.id}`}><span className="used-by-kind">Report</span><Link to={`/reports/${r.id}`}>{r.name}</Link></li>)}
                  {usedBy.kpis.map((k) => <li key={`k-${k.id}`}><span className="used-by-kind">KPI</span><Link to={`/kpis/${k.id}`}>{k.name}</Link></li>)}
                  {usedBy.alerts.map((a) => <li key={`a-${a.id}`}><span className="used-by-kind">Alert</span><Link to="/alerts">{a.name}</Link></li>)}
                </ul>
              )}
            </section>
          )}

          <section className="source-card">
            <h3>Sample rows</h3>
            {sample.length === 0 ? <p className="dataset-empty">No rows in the current load.</p> : (
              <div className="dataset-sample-wrapper">
                <table className="dataset-sample-table">
                  <thead><tr>{Object.keys(sample[0]).map((c) => <th key={c}>{c}</th>)}</tr></thead>
                  <tbody>
                    {sample.map((row, i) => (
                      <tr key={i}>{Object.keys(sample[0]).map((c) => <td key={c}>{row[c] === null || row[c] === undefined ? '' : String(row[c])}</td>)}</tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </section>

          {isAdmin && (
            <section className="source-card source-danger" aria-labelledby="delete-dataset-heading">
              <h3 id="delete-dataset-heading">Delete dataset</h3>
              <p>Removes {name} and all its rows. Reports, KPIs and statements built on it show an error until a dataset with this name is defined again.</p>
              <label>
                <span>Type <strong>{name}</strong> to confirm</span>
                <input value={confirmDelete} onChange={(e) => setConfirmDelete(e.target.value)} aria-label="Type the dataset name to confirm deletion" />
              </label>
              <button type="button" className="btn-danger btn-small" disabled={confirmDelete !== name} onClick={remove}>Delete dataset</button>
            </section>
          )}
        </>
      )}
    </div>
  );
};

export default DatasetDetailPage;
